import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import { Toaster } from 'react-hot-toast';
import { useAuth, useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { useTechWatchNotifications } from '@/hooks/useTechWatchNotifications';
import { BlueDot, Highlight, OnboardingWelcome } from '@/components/tutorial';
import { useTutorial } from '@/context/TutorialContext';
import { PageHeader } from '@/components/ui/PageHeader';
import NotificationDropdown from './NotificationDropdown';
import UserProfileDropdown from './UserProfileDropdown';
import BottomNav from './BottomNav';
import {
  getPageMeta,
  isNavigationItemActive,
  navigationSections,
  type NavigationItem,
  type NavigationSection,
} from './appShellConfig';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const { onboardingState, isFirstTimeUser } = useTutorial();
  const [showWelcome, setShowWelcome] = useState(false);
  const pageMeta = getPageMeta(location.pathname);

  useTechWatchNotifications(2 * 60 * 1000, true);

  useEffect(() => {
    if (isFirstTimeUser()) {
      setShowWelcome(true);
    }
  }, [onboardingState, isFirstTimeUser]);

  useEffect(() => {
    document.title = `${pageMeta.title} | SDLC Tracker`;
  }, [pageMeta.title]);

  const filteredNavigationSections = navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.permission && !hasPermission(item.permission)) {
          return false;
        }

        if (item.roles && !item.roles.includes(user?.role || '')) {
          return false;
        }

        return true;
      }),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className={`fixed inset-0 z-40 flex md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`relative flex max-w-xs flex-1 flex-col bg-white transition duration-300 ease-in-out dark:bg-gray-800 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="absolute right-0 top-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigationSections={filteredNavigationSections} currentPath={location.pathname} />
        </div>
      </div>

      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white shadow-lg dark:bg-gray-800">
          <SidebarContent navigationSections={filteredNavigationSections} currentPath={location.pathname} />
        </div>
      </div>

      <div className="flex flex-1 flex-col md:pl-64">
        <div className="sticky top-0 z-10 bg-gray-50 pl-1 pt-1 dark:bg-gray-900 md:hidden sm:pl-3 sm:pt-3">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>

        <header className="border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <PageHeader
                size="sm"
                eyebrow={pageMeta.eyebrow || 'SDLC Tracker'}
                title={pageMeta.title}
                description={pageMeta.description}
              />

              <div className="flex items-center gap-3 self-end lg:self-auto">
                <button
                  onClick={toggleTheme}
                  aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                  className="rounded-lg p-2 text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-white"
                >
                  {theme === 'dark' ? (
                    <SunIcon className="h-5 w-5" />
                  ) : (
                    <MoonIcon className="h-5 w-5" />
                  )}
                </button>

                <NotificationDropdown />
                <UserProfileDropdown />
              </div>
            </div>
          </div>
        </header>

        <main className="relative flex-1 overflow-y-auto pb-16 md:pb-0 focus:outline-none">
          <div className="py-6">
            <div className="w-full px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>

        <BottomNav onOpenSidebar={() => setSidebarOpen(true)} />

        <Toaster
          position="top-right"
          toastOptions={{ duration: 4000 }}
          containerStyle={{ zIndex: 10000, marginTop: '1rem', marginRight: '1rem' }}
        />
        <Highlight />
        {showWelcome && onboardingState?.firstTimeUser && (
          <OnboardingWelcome onClose={() => setShowWelcome(false)} />
        )}
      </div>
    </div>
  );
};

interface SidebarContentProps {
  navigationSections: NavigationSection[];
  currentPath: string;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ navigationSections, currentPath }) => {
  return (
    <>
      <div className="flex h-16 flex-shrink-0 items-center bg-primary-600 px-4">
        <ShieldCheckIcon className="h-8 w-8 text-white" />
        <span className="ml-2 text-lg font-semibold text-white">SDLC Tracker</span>
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <nav className="flex-1 space-y-6 px-3 py-4">
          {navigationSections.map((section) => (
            <div key={section.id}>
              <h2 className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                {section.title}
              </h2>
              <div className="mt-2 space-y-1">
                {section.items.map((item) => (
                  <SidebarItem key={item.name} item={item} currentPath={currentPath} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

interface SidebarItemProps {
  item: NavigationItem;
  currentPath: string;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ item, currentPath }) => {
  const isActive = isNavigationItemActive(currentPath, item.href);

  return (
    <Link
      to={item.href}
      className={`group flex items-center rounded-xl px-3 py-2.5 min-h-[44px] text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
      } ${item.navClass || ''}`}
    >
      <item.icon
        className={`mr-3 h-5 w-5 flex-shrink-0 ${
          isActive
            ? 'text-primary-500 dark:text-primary-400'
            : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
        }`}
      />
      <span className="relative inline-flex items-center gap-2">
        {item.name}
        {item.blueDotId && <BlueDot id={item.blueDotId} label={item.name} />}
      </span>
    </Link>
  );
};

export default Layout;
